import type { NavigateFunction } from "react-router-dom";
import type { Config } from "config/config";
import type { AnalyticsManager } from "services/analytics/analyticsManager";
import type { CompanyClient } from "services/company/companyClient";
import { type CompanyData, CompanyNamePostfix } from "services/company/companyTypes";
import type { LeadClient } from "services/lead/leadClient";
import { PricingPlan } from "services/pricingPlan/pricingPlanTypes";
import { SignUpPageStoreBase } from "services/signUpPage/signUpPageStoreBase";
import { flowTypeUrlParam, parseFlowTypeUrlParam } from "services/user/flowTypeUrlParam";
import type { UserStore } from "services/user/userStore";

const pricingPlanUrlParam = "pricing_plan";

export class SignUpPageStore extends SignUpPageStoreBase {
  protected _defaultPostfix = CompanyNamePostfix.PtyLtdV1;

  constructor(
    protected readonly _companyClient: CompanyClient,
    protected readonly _leadClient: LeadClient,
    protected readonly _analyticsManager: AnalyticsManager,
    protected readonly _userStore: UserStore,
    protected readonly _navigate: NavigateFunction,
    protected readonly _config: Config,
  ) {
    super(_companyClient, _leadClient, _analyticsManager, _userStore, _navigate, _config);
  }

  protected async _proceedSignUp(): Promise<undefined | CompanyData> {
    const company = await super._proceedSignUp();

    await this._updatePricingPlan(this._extractSearchParams(), company);

    return company;
  }

  protected _navigateToFinalPage(): void {
    const rawFlowType = this._extractSearchParams().get(flowTypeUrlParam);

    if (rawFlowType && parseFlowTypeUrlParam(rawFlowType)) {
      this._navigate({ pathname: this.finalPage, search: `${flowTypeUrlParam}=${rawFlowType}` });
      return;
    }

    super._navigateToFinalPage();
  }

  private async _updatePricingPlan(searchParams: URLSearchParams, company: CompanyData | undefined): Promise<void> {
    if (!company) {
      return;
    }

    const pricingPlan = searchParams.get(pricingPlanUrlParam); // 'basic'

    if (!pricingPlan) {
      return;
    }

    switch (pricingPlan) {
      case "basic":
        await this._changePricingPlan(company, PricingPlan.EasyCompany);
        break;
      default:
        // Unrecognised plan is ignored
        this._analyticsManager.event("signup-with-unknown-pricing-plan-error", { pricingPlan });
        break;
    }
  }
}
